import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faUser, faPlusCircle, faSearch } from '@fortawesome/free-solid-svg-icons';

import Paper from '@material-ui/core/Paper';
import { Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import Modal from '../../../components/Modal';
import Loader from '../../../components/Loader';
import MTable from '../../../components/MTable';
import Filter from '../../../components/Filter/components';
import FilterMobile from '../../../components/FilterMobile/components';
import TableMobile from '../../../components/TableMobile';
import Input from '../../../components/Input';
import Button from '../../../components/Button';

import { loadEmployees, loadEmployeeByEmail } from '../store/thunk';
import { loadProfile } from '../../Profile/store/thunk';
import { loadFilters } from '../../../components/Filter/store/thunk';

import { setIsEditButtonClicked } from '../store/actions';
import { setEmployeeId } from '../../EmployeesRegistration/store/actions';

import { formatFilters } from '../../../helpers/formatters';

import useDidMount from '../../../helpers/hooks/useDidMount';
import { RESTRICTION_PAGE } from '../../../helpers/restrictions';
import { emailRegExp } from '../../../helpers/regex';

const PencilIcon = require('../../../assets/img/icon_pencil.svg');

const headers = [
  { title: 'UserID', field: 'userId', sorting: false },
  { title: 'Nome', field: 'name', sorting: false },
  { title: 'Telefone', field: 'phone', sorting: false },
  { title: 'E-mail', field: 'email', sorting: false },
  { title: 'Cargo', field: 'profileName', sorting: false },
  { title: 'Situação', field: 'status', sorting: false },
  { title: 'Revenda', field: 'resaleName', sorting: false }
];

const noUserIdHeaders = [
  { title: 'Nome', field: 'name', sorting: false },
  { title: 'Telefone', field: 'phone', sorting: false },
  { title: 'E-mail', field: 'email', sorting: false },
  { title: 'Cargo', field: 'profileName', sorting: false },
  { title: 'Situação', field: 'status', sorting: false }
];

const width = window.innerWidth;
const tableRowWidth = width < 768 ? 120 : 100;

export default function EmployeesList({ handlePreviousStep }) {
  const dispatch = useDispatch();

  const history = useHistory();

  const didComponentMount = useDidMount();

  const pathSegments = history.location.pathname.split('/');
  const isInitialRegistration = pathSegments.includes('cadastro-inicial') && pathSegments.includes('usuarios');

  const {
    userId,
    userRestrictions,
    submit: { isAdminUser }
  } = useSelector(state => state.profile);

  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selects, qp } = useSelector(state => state.filters);
  const { list, isLoading, total } = useSelector(state => state.employeesList);

  const filters = formatFilters(['status', 'profile'], selects);

  const isUserAdmin = +isAdminUser == 1;

  const jumpResales = selects
    ?.find(select => select?.id == 'resale')?.options
    ?.filter(option => !!option?.id);

  const formattedProfiles = filters
    ?.find(filter => filter?.id == 'profile')?.options
    ?.map(option => {
      return option?.value == ''
        ? {
          label: '--',
          value: ''
        } : {
          label: option?.name,
          value: option?.value
        }
    });

  const formattedFilters = filters?.map(filter => {
    return filter?.id == 'profile'
      ? {
        ...filter,
        options: formattedProfiles
      } : {
        ...filter
      }
  });

  const [isSearchUserModalOpen, setIsSearchUserModalOpen] = useState(false);
  const [searchUserEmail, setSearchUserEmail] = useState('');
  const [searchUserEmailError, setSearchUserEmailError] = useState('');

  const [employeesPage, setEmployeesPage] = useState(1);
  const employeesPageSize = 10;
  const employeesPageCount = Math.ceil(total / employeesPageSize);

  const formattedList = list.map(employee => {
    return {
      ...employee,
      profileName: formattedProfiles?.filter(profile => profile.value == employee.profileId)[0]?.label,
      resaleName: jumpResales?.find(resale => resale?.id == employee?.resaleId)?.label || '-',
      name: +employee?.mainUser == 1 && isUserAdmin
        ? (
          <>
            {employee?.name}
            <FontAwesomeIcon
              icon={faUser}
              color="#2CA9D6"
              size="xs"
              style={{ marginLeft: 10 }}
            />
          </>
        )
        : employee?.name
    }
  });

  const setTableRowColor = row => {
    const style = {
      backgroundColor: '#FFFFFF'
    }

    if(+row?.mainUser == 1 && isUserAdmin) {
      style.backgroundColor = '#DEE7FC';
    }

    return style;
  }

  useEffect(() => {
    dispatch(loadProfile(userId));
  }, []);

  useEffect(() => {
    if(userRestrictions?.includes(RESTRICTION_PAGE.VIEW_USER)) {
      toast.error('Ação não autorizada.');
      history.push('/visao-geral');
    }
  }, [userRestrictions]);

  useEffect(() => {
    dispatch(loadEmployees({ page: 1, qp: '', userId, establishmentId }));
    dispatch(loadFilters(userId, establishmentId));
  }, [userId, establishmentId]);

  useEffect(() => {
    if(!didComponentMount) {
      handleLoadEmployees({ page: employeesPage, qp, userId, establishmentId });
    }
  }, [employeesPage]);

  const handleLoadEmployees = ({ page, qp }) => {
    dispatch(loadEmployees({ page, qp, userId, establishmentId }));
  }

  const handleUpdateEmployees = rowData => {
    dispatch(setEmployeeId(rowData.employeeId));
    dispatch(setIsEditButtonClicked(true));

    if(isInitialRegistration) {
      if(+rowData.mainUser == 1) {
        return toast.error('Edite seu usuário após terminar os primeiros passos.');
      }

      return handlePreviousStep();
    }

    history.push('/editar-usuario');
  }

  const width = window.innerWidth;
  const isMobile = width < 1024;

  return(
    <>
      {isSearchUserModalOpen && (
        <Modal
          id='search-user-modal'
          maxWidth='sm'
          title='Buscar usuário'
          open={isSearchUserModalOpen}
          onClose={() => setIsSearchUserModalOpen(false)}
        >
          <Loader isLoading={isLoading}>
            <Grid container>
              <Grid item xs={12}>
                <Input
                  id='search-user-inpunt'
                  name='email'
                  label='Email'
                  value={searchUserEmail}
                  error={!!searchUserEmailError}
                  helperText={searchUserEmailError}
                  onChange={event => setSearchUserEmail(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
                <Button
                  type="button"
                  color="error"
                  onClick={() => setIsSearchUserModalOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  color="success"
                  onClick={() => {
                    setSearchUserEmailError('');

                    if(!searchUserEmail) {
                      return setSearchUserEmailError('Insira um email');
                    }

                    if(!emailRegExp.test(searchUserEmail)) {
                      return setSearchUserEmailError('O email precisa ser válido');
                    }

                    dispatch(loadEmployeeByEmail(userId, searchUserEmail)).then(({ userId }) => {
                      handleUpdateEmployees({ employeeId: userId });
                    });
                  }}
                >
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </Loader>
        </Modal>
      )}
      {(isMobile && !userRestrictions?.includes(RESTRICTION_PAGE.CREATE_USER)) && (
        <Grid container style={{ marginBottom: 10 }}>
          <Grid item xs={12}>
            <Button
              fullWidth
              type="button"
              color="success"
              startIcon={
                <FontAwesomeIcon
                  icon={faPlusCircle}
                  color="#FFFFFF"
                  size="xs"
                />
              }
              onClick={() => {
                if(isInitialRegistration) {
                  dispatch(setEmployeeId(''));
                  dispatch(setIsEditButtonClicked(false));
                  return handlePreviousStep();
                }

                history.push('/novo-usuario');
              }}
            >
              Cadastrar usuário
            </Button>
          </Grid>
          {isUserAdmin && (
            <Grid item xs={12} style={{ marginTop: 10 }}>
              <Button
                fullWidth
                type="button"
                color="success"
                startIcon={
                  <FontAwesomeIcon
                    icon={faSearch}
                    color="#FFFFFF"
                    size="xs"
                  />
                }
                onClick={() => setIsSearchUserModalOpen(true)}
              >
                Buscar usuário
              </Button>
            </Grid>
          )}
        </Grid>
      )}
      <Paper>
        {isMobile ? (
          <FilterMobile
            filter
            hasSearchTerm
            filters={formattedFilters}
            handlePageRequest={handleLoadEmployees}
          />
        ) : (
          <Filter
            filter
            hasSearchTerm
            filters={formattedFilters}
            actionButton={
              <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 10 }}>
                {!userRestrictions?.includes(RESTRICTION_PAGE.CREATE_USER) && (
                  <Button
                    type="button"
                    color="success"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faPlusCircle}
                        color="#FFFFFF"
                        size="xs"
                      />
                    }
                    onClick={() => {
                      if(isInitialRegistration) {
                        dispatch(setEmployeeId(''));
                        dispatch(setIsEditButtonClicked(false));
                        return handlePreviousStep();
                      }

                      history.push('/novo-usuario');
                    }}
                  >
                    Adicionar usuário
                  </Button>
                )}
                {isUserAdmin && (
                  <Button
                    type="button"
                    color="success"
                    startIcon={
                      <FontAwesomeIcon
                        icon={faSearch}
                        color="#FFFFFF"
                        size="xs"
                      />
                    }
                    onClick={() => setIsSearchUserModalOpen(true)}
                  >
                    Buscar usuário
                  </Button>
                )}
              </div>
            }
            handlePageRequest={handleLoadEmployees}
          />
        )}
        {isMobile ? (
          <TableMobile
            loading={isLoading}
            data={formattedList?.map(list => {
              return {
                ...list,
                ...isUserAdmin && {
                  userId: {
                    index: 1,
                    label: 'UserID',
                    value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.userId}</Typography>
                  }
                },
                name: {
                  index: 2,
                  label: 'Nome',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.name}</Typography>
                },
                phone: {
                  index: 3,
                  label: 'Telefone',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.phone}</Typography>
                },
                email: {
                  index: 4,
                  label: 'Email',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.email}</Typography>
                },
                profileName: {
                  index: 5,
                  label: 'Cargo',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.profileName}</Typography>
                },
                status: {
                  index: 6,
                  label: 'Situação',
                  value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.status}</Typography>
                },
                ...isUserAdmin && {
                  resaleName: {
                    index: 7,
                    label: 'Revenda',
                    value: <Typography color='textSecondary' variant='body2' style={{ wordBreak: 'break-all' }}>{list?.resaleName}</Typography>
                  }
                }
              }
            })}
            actions={[
              {
                action: rowData => (
                  <Tooltip placement='top' title='Editar usuário' enterTouchDelay={0}>
                    <IconButton
                      disabled={userRestrictions?.includes(RESTRICTION_PAGE.EDIT_USER)}
                      onClick={() => handleUpdateEmployees(rowData)}
                    >
                      <img
                        src={PencilIcon}
                        style={{
                          opacity: userRestrictions?.includes(RESTRICTION_PAGE.EDIT_USER)
                            ? 0.5
                            : 1
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )
              }
            ]}
          />
        ) : (
          <MTable
            hasSorting
            loading={isLoading}
            headers={+isAdminUser == 1 ? headers : noUserIdHeaders}
            data={formattedList}
            rowStyle={setTableRowColor}
            actions={[
              {
                icon: () => (
                  <FontAwesomeIcon
                    icon={faPen}
                    color="#022A5C"
                    size="xs"
                    style={{
                      opacity: userRestrictions?.includes(RESTRICTION_PAGE.EDIT_USER)
                        ? 0.5
                        : 1
                    }}
                  />
                ),
                tooltip: 'Editar usuário',
                onClick: (_, rowData) => handleUpdateEmployees(rowData),
                disabled: userRestrictions?.includes(RESTRICTION_PAGE.EDIT_USER),
              }
            ]}
            actionColumnWidth={tableRowWidth}
          />
        )}
        <Grid
          container
          xs={12}
          style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
        >
          <Pagination
            color="primary"
            variant="outlined"
            shape="rounded"
            count={employeesPageCount}
            page={employeesPage}
            onChange={(_, page) => setEmployeesPage(page)}
          />
        </Grid>
      </Paper>
    </>
  );
}